import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { ClientType } from 'src/app/enums/client.enum';
import { ClientFactory } from 'src/app/factories/client/client-factory';
import { IClient } from 'src/app/factories/client/client.inteface';

@Injectable({
  providedIn: 'root'
})
export class ClientService {
  private currentClientSubject: BehaviorSubject<string | null>;
  public currentClient$: Observable<string | null>;

  constructor(private router: Router) {
    this.currentClientSubject = new BehaviorSubject<string | null>(this.getStoredClient());
    this.currentClient$ = this.currentClientSubject.asObservable();
  }

  /**
     * Get all available client types
     */
  getAvailableClientTypes(): string[] {
    return Object.values(ClientType);
  }

  /**
   * Check if a client type is valid
   */
  isValidClientType(clientType: string): boolean {
    return Object.values(ClientType).includes(clientType.toLowerCase() as ClientType);
  }

  /**
   * Extract client from current URL
   */
  getClientFromUrl(): string | null {
    const urlSegments = this.router.url.split('/').filter(segment => segment);
    if (urlSegments.length > 0 && this.isValidClientType(urlSegments[0])) {
      return urlSegments[0];
    }
    return null;
  }

  /**
   * Get the current active client
   */
  getCurrentClient(): string | null {
    return this.currentClientSubject.value;
  }

  /**
   * Set the current client (called during login)
   */
  setCurrentClient(client: string): void {
    if (this.isValidClientType(client)) {
      localStorage.setItem('current_client', client);
      this.currentClientSubject.next(client);
    } else {
      console.error('Invalid client type:', client);
    }
  }

  /**
   * Get stored client from localStorage
   */
  getStoredClient(): string | null {
    if (typeof localStorage !== 'undefined') {
      const storedClient = localStorage.getItem('current_client');
      return storedClient && this.isValidClientType(storedClient) ? storedClient : null;
    }
    return null;
  }

  /**
   * Validate if URL client matches stored client
   * Returns true if valid, false if mismatch
   */
  validateClientFromUrl(): boolean {
    const urlClient = this.getClientFromUrl();
    console.log("urlClient=>",urlClient);
    
    const storedClient = this.getStoredClient();

    // If no stored client, this might be first visit - allow it
    if (!storedClient) {
      return true;
    }

    // If no client in URL, it's invalid for authenticated users
    if (!urlClient) {
      return false;
    }

    // Check if they match
    return urlClient === storedClient;
  }

  /**
   * Get client instance using factory pattern
   */
  getClient(client?: string): IClient | null {
    const clientToUse = client || this.getCurrentClient();
    if (!clientToUse) {
      return null;
    }
    try {
      return ClientFactory.createClient(clientToUse);
    } catch (error) {
      console.error('Error creating client:', error);
      return null;
    }
  }

  /**
   * Get client configuration (single source of truth for all client data)
   */
  getClientConfig(client?: string) {
    const clientInstance = this.getClient(client);
    return clientInstance ? clientInstance.getConfig() : null;
  }

  /**
   * Clear client data (on logout)
   */
  clearClient(): void {
    localStorage.removeItem('current_client');
    this.currentClientSubject.next(null);
  }

  /**
   * Get all available clients
   */
  getAvailableClients(): string[] {
    return this.getAvailableClientTypes();
  }

  /**
   * Build URL with client prefix
   */
  buildClientUrl(path: string, client?: string): string {
    const clientToUse = client || this.getCurrentClient();
    if (!clientToUse) {
      return path;
    }
    // Remove leading slash from path if present
    const cleanPath = path.startsWith('/') ? path.substring(1) : path;
    return `/${clientToUse}/${cleanPath}`;
  }

  /**
   * Navigate to a path with client prefix
   */
  navigateWithClient(path: string[], client?: string): void {
    const clientToUse = client || this.getCurrentClient();
    if (clientToUse) {
      this.router.navigate([clientToUse, ...path]);
    } else {
      this.router.navigate(path);
    }
  }
}